import { Clear, FilterList, People } from '@mui/icons-material'
import {
  Button,
  Checkbox,
  FormControlLabel,
  Menu,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SquareButton } from '../../../styles/ButtonStyles'
import { RequestProps, User } from '../../../store/types'
import { isEqual } from 'lodash'
import LoadingWrapper from '../../Loading/LoadingWrapper'
import { useLeftPadding } from '../../../store/redux/hooks'
import EmployeeCheckboxList from './EmployeeCheckboxList'
import checkIsDataIndeterminate from '../../../store/utils/checkIsDataIndeterminate'

type FilterMenteesMenuPopupProps = {
  requestProps: RequestProps
  setRequestProps: React.Dispatch<React.SetStateAction<RequestProps>>
  allMentees: User[] | undefined
  isMenteesLoading: boolean
  restEmployees: User[] | undefined
  isEmployeesLoading: boolean
}

const FilterMenteesMenuPopup = ({
  requestProps,
  setRequestProps,
  allMentees,
  isMenteesLoading,
  restEmployees,
  isEmployeesLoading,
}: FilterMenteesMenuPopupProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const [selectedMentees, setSelectedMentees] = useState<string[]>([])
  const [selectedOthers, setSelectedOthers] = useState<string[]>([])

  const [isAllMentees, setIsAllMentees] = useState<boolean>(true)
  const [isMenteesIndeterminate, setIsMenteesIndeterminate] = useState<boolean>(false)

  const [isAllOthers, setIsAllOthers] = useState<boolean>(false)
  const [isOthersIndeterminate, setIsOthersIndeterminate] = useState<boolean>(false)

  const otherEmployees = useMemo(() => {
    const menteeIds = new Set(allMentees?.map(mentee => mentee.id) ?? [])

    return (restEmployees ?? []).filter(employee => !menteeIds.has(employee.id))
  }, [allMentees, restEmployees])

  useEffect(() => {
    if (!isMenteesLoading && allMentees) setSelectedMentees(allMentees.map(mentee => mentee.id))
    setIsAllMentees(true)
  }, [allMentees, isMenteesLoading])

  useEffect(() => {
    setIsAllMentees(allMentees ? selectedMentees.length >= allMentees.length : false)
    setIsMenteesIndeterminate(allMentees ? checkIsDataIndeterminate(selectedMentees, allMentees) : false)
  }, [allMentees, selectedMentees])

  useEffect(() => {
    setIsAllOthers(selectedOthers.length >= otherEmployees.length)
    setIsOthersIndeterminate(checkIsDataIndeterminate(selectedOthers, otherEmployees))
  }, [otherEmployees, selectedOthers])

  useEffect(() => {
    if (!isMenteesLoading && allMentees && isAllMentees) setSelectedMentees(allMentees.map(mentee => mentee.id))
  }, [allMentees, isAllMentees, isMenteesLoading])

  const handleMenteeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const isChecked = e.target.checked

    setSelectedMentees(prevMentee => {
      const cleanedPrev = prevMentee.length === 1 && prevMentee[0] === '' ? [] : prevMentee

      return isChecked ? [...cleanedPrev, value] : cleanedPrev.filter(mentee => mentee !== value)
    })
  }

  const handleOtherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const isChecked = e.target.checked

    setSelectedOthers(prevOther => {
      const cleanedPrev = prevOther.length === 1 && prevOther[0] === '' ? [] : prevOther

      return isChecked ? [...cleanedPrev, value] : cleanedPrev.filter(other => other !== value)
    })
  }

  const markAllMentees = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isAllMentees || isMenteesIndeterminate) {
      setSelectedMentees([''])
      setIsAllMentees(false)
      setIsMenteesIndeterminate(false)
    } else if (allMentees) setSelectedMentees(allMentees.map(mentee => mentee.id))
  }

  const markAllOthers = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isAllOthers || isOthersIndeterminate) {
      setSelectedOthers([''])
      setIsAllOthers(false)
      setIsOthersIndeterminate(false)
    } else setSelectedOthers(otherEmployees.map(employee => employee.id))
  }

  const handleFilter = () => {
    let newProps: RequestProps = { ...requestProps }
    newProps = { ...newProps, menteesIds: selectedMentees.concat(selectedOthers) }

    if (!isEqual(requestProps, newProps)) {
      setRequestProps({ ...newProps })
    }
  }

  const handleClear = () => {
    setSelectedMentees([''])
    setSelectedOthers([''])

    let newProps: RequestProps = { ...requestProps }
    newProps = { ...newProps, menteesIds: [''] }
    setRequestProps({ ...newProps })
  }

  const leftPadding = useLeftPadding()

  const isFilterDisabled = useMemo(() => {
    return !selectedMentees.concat(selectedOthers).some(id => id !== '')
  }, [selectedMentees, selectedOthers])

  return (
    <PopupState
      popupId={`filter-mentees-popup-menu`}
      variant="popover"
      disableAutoFocus={false}
      parentPopupState={null}
    >
      {popupState => (
        <>
          <Tooltip title={t('page.leaderView.mentees')}>
            <SquareButton color="secondary" size="small" {...bindTrigger(popupState)}>
              <People />
            </SquareButton>
          </Tooltip>

          <Menu
            {...bindMenu(popupState)}
            slotProps={{
              paper: {
                elevation: 3,
                sx: {
                  maxHeight: 450,
                  height: 'auto',
                  width: 'auto',
                  overflowY: 'hidden',
                  paddingY: '8px',
                  paddingX: '16px',
                  marginTop: '8px',
                },
              },
            }}
          >
            <LoadingWrapper isLoading={isMenteesLoading || isEmployeesLoading}>
              <Stack>
                <Stack sx={{ maxHeight: 350, overflowY: 'auto', pl: leftPadding, ml: `-${leftPadding}`, gap: 2 }}>
                  <Stack>
                    {allMentees && allMentees?.length > 1 && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            onChange={markAllMentees}
                            checked={isAllMentees}
                            indeterminate={isMenteesIndeterminate}
                          />
                        }
                        label={
                          <Typography variant="h6" component="span" color="text.mediumEmphasis">
                            {t('page.leaderView.yourMentees')}
                          </Typography>
                        }
                      />
                    )}
                    {allMentees && allMentees.length === 1 && (
                      <Typography variant="h6" component="span" color="text.mediumEmphasis">
                        {t('page.leaderView.yourMentees')}
                      </Typography>
                    )}
                    <EmployeeCheckboxList
                      employees={allMentees || []}
                      selectedMentees={selectedMentees}
                      handleMenteeChange={handleMenteeChange}
                      isDownSm={isDownSm}
                    />
                  </Stack>
                  <Stack>
                    {otherEmployees.length > 1 && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            onChange={markAllOthers}
                            checked={isAllOthers}
                            indeterminate={isOthersIndeterminate}
                          />
                        }
                        label={
                          <Typography variant="h6" component="span" color="text.mediumEmphasis">
                            {t('page.projectDistribution.otherEmployees')}
                          </Typography>
                        }
                      />
                    )}
                    {otherEmployees.length === 1 && (
                      <Typography variant="h6" component="span" color="text.mediumEmphasis">
                        {t('page.projectDistribution.otherEmployees')}
                      </Typography>
                    )}
                    <EmployeeCheckboxList
                      employees={otherEmployees}
                      selectedMentees={selectedOthers}
                      handleMenteeChange={handleOtherChange}
                      isDownSm={isDownSm}
                    />
                  </Stack>
                </Stack>
                <Stack justifyContent="center" sx={{ flexDirection: isDownSm ? 'column' : 'row-reverse', gap: 1 }}>
                  <Button variant="contained" size="small" onClick={handleFilter} disabled={isFilterDisabled}>
                    <FilterList /> {t('page.users.filtering.filter')}
                  </Button>
                  <Button variant="text" size="small" onClick={handleClear}>
                    <Clear /> {t('page.users.filtering.clear')}
                  </Button>
                </Stack>
              </Stack>
            </LoadingWrapper>
          </Menu>
        </>
      )}
    </PopupState>
  )
}

export default FilterMenteesMenuPopup
